// SPDX-License-Identifier: MIT
// Wiring for desktop ↔ cloud sync: composes the real local store (desktop data
// bridge), the real cloud store (Supabase project API), and the durable syncmap.
// Pure composition over the unit-tested core (cloud-sync.ts) — kept thin.
import { supabase } from '../auth/supabase';
import {
  listProjects as cloudListProjects,
  saveProject as cloudSaveProject,
  listProjectVersions as cloudListVersions,
  saveProjectVersionAt as cloudSaveVersionAt,
  pushBackendConfig,
  pushForgejoRepoUrl,
  saveDiscoveryAnswers as cloudSaveDiscoveryAnswers,
  listProjectSessions as cloudListSessions,
  upsertProjectSession as cloudUpsertSession,
} from '../projects/api';
import { loadAllSessions, saveSessions, type Session } from './session-store';
import { stripSessionForCloud, mergeSessions, type CloudSession, type CloudSessionRequest } from './session-sync';
import { listAttachments as cloudListAttachments, getSignedUrl as cloudSignedUrl } from '../projects/attachments';
import { desktopDataBridge, applyCloudDiscovery, applyCloudBackend, applyCloudDeploy, applyCloudForgejoUrl } from './desktop-data-bridge';
import { versionsToPush } from './cloud-sync';
import { desktopAttachmentBridge } from './desktop-attachment-bridge';
import { appendVersion, listVersionsForProject } from './version-store';
import { syncProjects, type SyncResult } from './cloud-sync';
import { loadSyncMap, saveSyncMap } from './syncmap-store';
import { setLastSyncedAt } from './sync-meta-store';
import { detectPlatform } from './index';
import { writeProjectEnv } from './desktop-delivery';

// True only when the desktop user has a Supabase session — sync is opt-in and
// requires the cloud account the projects sync TO.
export async function isCloudSyncAvailable(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return !!data.session;
}

export async function runCloudSync(): Promise<SyncResult> {
  const local = {
    listProjects: () => desktopDataBridge.listProjects(),
    saveProject: (r: Parameters<typeof cloudSaveProject>[0], i: string, l: Parameters<typeof cloudSaveProject>[2]) =>
      desktopDataBridge.saveProject(r, i, l),
  };
  const cloud = {
    listProjects: () => cloudListProjects(),
    // origin='desktop_sync': rows pushed from desktop are NOT metered by the
    // CP-1 quota trigger (desktop licence = unlimited local ideas).
    saveProject: (r: Parameters<typeof cloudSaveProject>[0], i: string, l: Parameters<typeof cloudSaveProject>[2]) =>
      cloudSaveProject(r, i, l, 'desktop_sync'),
  };
  return syncProjects(local, cloud, { load: loadSyncMap, save: saveSyncMap });
}

// ─── cloud-sync v2: also pull a project's magnify VERSIONS + ATTACHMENTS ─────────
// v1 synced only the project entity. For real dogfooding the desktop also needs the
// version history and the uploaded files. A pulled project gets a NEW local id, so
// the syncmap (localId→cloudId) is the bridge: we re-point cloud versions/attachments
// onto the local id. Pull-only + idempotent (dedupe vs what's already local).

export interface DesktopSyncResult {
  projects: number;        // projects pulled from cloud
  versions: number;        // version rows pulled
  versionsPushed: number;  // NEW — pushed desktop→cloud
  attachments: number;     // attachments downloaded
  backends: number;        // backend-creds slices reconciled (pulled web→desktop + pushed desktop→web)
  sessions: number;        // NEW — build-session history rows reconciled (push + pull, metadata only)
}
/** @deprecated name kept for back-compat */
export type DesktopPullResult = DesktopSyncResult;

// Pull cloud project_versions for one project, re-pointed onto the local id.
// Dedup by version_number so repeated syncs add nothing.
async function pullVersionsFor(localId: string, cloudId: string): Promise<number> {
  let pulled = 0;
  try {
    const cloudVersions = await cloudListVersions(cloudId);
    const have = new Set((await listVersionsForProject(localId)).map((v) => v.version_number));
    for (const v of cloudVersions) {
      if (have.has(v.version_number)) continue;
      await appendVersion({ ...v, project_id: localId }); // re-point cloud→local id
      have.add(v.version_number);
      pulled++;
    }
  } catch (err) {
    console.warn(`[sync] versions pull failed for ${localId}:`, err);
  }
  return pulled;
}

// Push local project_versions whose version_number the cloud lacks (fill-missing).
// Mirror of pullVersionsFor. Idempotent (sync_project_version ON CONFLICT DO NOTHING).
async function pushVersionsFor(localId: string, cloudId: string): Promise<number> {
  let pushed = 0;
  try {
    const localVersions = await listVersionsForProject(localId);
    const cloudNums = (await cloudListVersions(cloudId)).map((v) => v.version_number);
    for (const v of versionsToPush(localVersions, cloudNums)) {
      await cloudSaveVersionAt(cloudId, v);
      pushed++;
    }
  } catch (err) {
    console.warn(`[sync] versions push failed for ${localId}:`, err);
  }
  return pushed;
}

// Push/pull agent build-session HISTORY for ONE folder (metadata only — the raw
// feed never leaves the machine, see session-sync.ts). Push local sessions the
// cloud lacks/has-fewer-requests-of (stripped of the feed), then merge cloud-only
// sessions into the local store so the other machine's history appears. user_id
// is filled by the DB default (auth.uid()). Returns the number reconciled.
async function pushPullSessionsForDir(dir: string, cloudId: string, local: Session[]): Promise<number> {
  let changed = 0;
  try {
    const cloudRows = await cloudListSessions(cloudId);
    const cloud: CloudSession[] = cloudRows.map((r) => ({
      id: r.id,
      started_at: r.started_at,
      requests: (Array.isArray(r.requests) ? r.requests : []) as CloudSessionRequest[],
    }));
    const cloudById = new Map(cloud.map((c) => [c.id, c]));
    let pushed = 0;
    for (const s of local) {
      const existing = cloudById.get(s.id);
      if (existing && existing.requests.length >= s.requests.length) continue;
      await cloudUpsertSession(cloudId, stripSessionForCloud(s));
      pushed++; changed++;
    }
    const merged = mergeSessions(local, cloud);
    const pulled = merged.length - local.length;
    if (pulled > 0) { await saveSessions(dir, merged); changed += pulled; }
    console.log(`[sync] sessions dir="${dir}" cloud=${cloudId.slice(0, 8)} local=${local.length} pushed=${pushed} pulled=${pulled}`);
  } catch (err) {
    console.warn(`[sync] sessions failed for dir ${dir}:`, err);
  }
  return changed;
}

// Push ONE project's build-session history to the cloud DB (metadata only —
// stripped of the raw feed, see session-sync.ts). Wired into the build-end
// auto-save (welcome.ts triggerCloudSave) so public.project_sessions populates
// automatically on every build — NOT only on a manual « Récupérer mes projets »
// (the gap that left project_sessions at 0 rows in prod: build history reached
// the repo carrier but never the DB index). Push-only (no pull/local write).
// Guarded: NEVER throws — a failed cloud backup must never block the build.
// Returns the number of sessions pushed. Requires a signed-in supabase session.
export async function pushSessionsForProject(cloudId: string, sessions: Session[]): Promise<number> {
  let pushed = 0;
  try {
    const cloudRows = await cloudListSessions(cloudId);
    const cloudReqLen = new Map<string, number>(
      cloudRows.map((r) => [r.id, (Array.isArray(r.requests) ? r.requests : []).length]),
    );
    for (const s of sessions) {
      const existing = cloudReqLen.get(s.id);
      // Skip only when the cloud already has this session with ≥ as many requests
      // (idempotent — re-pushing an unchanged session is a no-op upsert).
      if (existing !== undefined && existing >= s.requests.length) continue;
      await cloudUpsertSession(cloudId, stripSessionForCloud(s));
      pushed++;
    }
  } catch (err) {
    console.warn(`[sync] build-end session push failed for ${cloudId.slice(0, 8)}:`, err);
  }
  return pushed;
}

// Normalise a path for matching (Windows is case-insensitive ; tolerate \ vs /
// and a trailing separator).
function normPath(p: string): string {
  return p.replace(/\\/g, '/').replace(/\/+$/, '').toLowerCase();
}
// Normalise a forgejo repo URL (drop trailing .git, lowercase, trim slash) — the
// STABLE key shared by the local row and the cloud row.
function normRepo(u: string): string {
  return u.replace(/\.git$/, '').replace(/\/+$/, '').toLowerCase();
}

// Feed-DRIVEN session sync: iterate the agent feed (keyed by the folder where the
// sessions actually live), and map each folder → its cloud project id by the
// STABLE repo URL (machine-independent), falling back to the sync-map then the
// project's own id. Robust to a missing/stale per-machine sync-map entry; surfaces
// the count to the UI so it's verifiable without devtools.
export async function syncAllSessions(
  map: Record<string, string>,
  localProjects: Array<{ id: string; local_dir?: string | null; forgejo_repo_url?: string | null }>,
  cloudProjects: Array<{ id: string; forgejo_repo_url?: string | null }> = [],
): Promise<number> {
  let changed = 0;
  try {
    const feed = await loadAllSessions(); // { dir: Session[] }
    // STABLE cloud resolution: repo URL → cloud id (machine-independent, immune to
    // a missing/stale per-machine sync-map entry — the bug that left ascendis at 0
    // because the V3 push had come from another machine).
    const repoToCloud = new Map<string, string>();
    for (const c of cloudProjects) if (c.forgejo_repo_url) repoToCloud.set(normRepo(c.forgejo_repo_url), c.id);
    // dir(normalised) → cloud id, preferring the repo-URL match, then the sync-map,
    // then the project's own id (same-id projects).
    const dirToCloud = new Map<string, string>();
    for (const p of localProjects) {
      if (!p.local_dir) continue;
      const byRepo = p.forgejo_repo_url ? repoToCloud.get(normRepo(p.forgejo_repo_url)) : undefined;
      dirToCloud.set(normPath(p.local_dir), byRepo ?? map[p.id] ?? p.id);
    }
    for (const [dir, sessions] of Object.entries(feed)) {
      if (sessions.length === 0) continue;
      const cloudId = dirToCloud.get(normPath(dir));
      if (!cloudId) {
        console.log(`[sync] sessions dir="${dir}" has ${sessions.length} session(s) but no project match — skipped`);
        continue;
      }
      changed += await pushPullSessionsForDir(dir, cloudId, sessions);
    }
  } catch (err) {
    console.warn('[sync] session sweep failed:', err);
  }
  return changed;
}

// Pull cloud attachments for one project: download each binary (signed URL),
// re-extract its text, and store it under the desktop key (userId='local'). The
// desktop bridge keeps {metadata + extracted text} — no binary on disk. Dedup by
// (name, size) so repeated syncs add nothing.
async function pullAttachmentsFor(localId: string, cloudId: string, cloudUserId: string): Promise<number> {
  let pulled = 0;
  try {
    const cloudAtts = await cloudListAttachments(cloudUserId, cloudId);
    const existing = await desktopAttachmentBridge.list('local', localId);
    const have = new Set(existing.map((a) => `${a.name}|${a.size}`));
    for (const a of cloudAtts) {
      if (have.has(`${a.name}|${a.size}`)) continue;
      try {
        const url = await cloudSignedUrl(a.path);
        const blob = await (await fetch(url)).blob();
        const file = new File([blob], a.name, { type: a.mime || blob.type });
        await desktopAttachmentBridge.upload('local', localId, file); // extracts text + persists
        have.add(`${a.name}|${a.size}`);
        pulled++;
      } catch (err) {
        console.warn(`[sync] attachment "${a.name}" pull failed:`, err);
      }
    }
  } catch (err) {
    console.warn(`[sync] attachments list failed for ${localId}:`, err);
  }
  return pulled;
}

// Full desktop sync: projects (v1) + versions pull+push + attachments (v2) +
// backend creds + forgejo_repo_url + deploy state + discovery answers (cloud-sync v2).
// Iterates the whole syncmap (idempotent) so a project pulled in an earlier run still
// gets its slices backfilled. Requires a signed-in session for the cloud reads.
export async function syncDesktopWithCloud(): Promise<DesktopSyncResult> {
  const projectResult = await runCloudSync(); // projects + populates the syncmap
  const map = await loadSyncMap();            // localId → cloudId
  const { data } = await supabase.auth.getSession();
  const cloudUserId = data.session?.user?.id ?? null;
  // Full cloud rows (incl. discovery_* + backend_* + deploy_*) so we can copy all
  // slices that projectRowToMagnifyResult drops on the project pull.
  const cloudById = new Map((await cloudListProjects()).map((p) => [p.id, p]));
  const localById = new Map((await desktopDataBridge.listProjects()).map((p) => [p.id, p]));
  let versions = 0;
  let versionsPushed = 0;
  let attachments = 0;
  let backends = 0;
  let sessions = 0;
  for (const [localId, cloudId] of Object.entries(map)) {
    const cloudRow = cloudById.get(cloudId);
    const localRow = localById.get(localId);
    if (cloudRow) {
      // discovery (summary/questions/etc.) — existing, fill-missing
      try { await applyCloudDiscovery(localId, cloudRow); }
      catch (err) { console.warn(`[sync] discovery pull failed for ${localId}:`, err); }
      // backend creds — pull web→desktop, fill-missing; if it filled a DELIVERED
      // project, bake the creds into its .env.local so the app just works.
      try {
        const filled = await applyCloudBackend(localId, cloudRow);
        if (filled && localRow?.local_dir && cloudRow.backend_url && cloudRow.backend_anon_key) {
          await writeProjectEnv(localRow.local_dir as string, cloudRow.backend_url as string, cloudRow.backend_anon_key as string);
        }
        if (filled) backends++;
      } catch (err) { console.warn(`[sync] backend creds pull failed for ${localId}:`, err); }
      // deploy state — pull web→desktop, fill-missing (inert until gen-deployer live)
      try { await applyCloudDeploy(localId, cloudRow); }
      catch (err) { console.warn(`[sync] deploy state pull failed for ${localId}:`, err); }
      // backend creds — push desktop→web, fill-missing (only when cloud trio is empty)
      try {
        const localComplete = Boolean(localRow?.backend_kind && localRow?.backend_url && localRow?.backend_anon_key);
        const cloudEmpty = !cloudRow.backend_kind && !cloudRow.backend_url && !cloudRow.backend_anon_key;
        if (localComplete && cloudEmpty) {
          await pushBackendConfig(
            cloudId,
            localRow!.backend_kind as 'byo-supabase' | 'genesis-rest' | 'full-supabase',
            localRow!.backend_url as string,
            localRow!.backend_anon_key as string,
          );
          backends++;
        }
      } catch (err) { console.warn(`[sync] backend creds push failed for ${cloudId}:`, err); }
      // discovery answers — push desktop→web, fill-missing (only when cloud answers empty)
      try {
        const localAnswers = localRow?.discovery_answers;
        const cloudAnswers = cloudRow.discovery_answers;
        const cloudAnswersEmpty = !cloudAnswers || Object.keys(cloudAnswers).length === 0;
        if (localAnswers && Object.keys(localAnswers).length > 0 && cloudAnswersEmpty) {
          await cloudSaveDiscoveryAnswers(cloudId, localAnswers);
        }
      } catch (err) { console.warn(`[sync] discovery answers push failed for ${cloudId}:`, err); }
      // forgejo_repo_url — pull cloud→desktop fill-missing
      try { await applyCloudForgejoUrl(localId, cloudRow); }
      catch (err) { console.warn(`[sync] forgejo url pull failed for ${localId}:`, err); }
      // forgejo_repo_url — push desktop→cloud fill-missing (only when cloud empty)
      try {
        if (localRow?.forgejo_repo_url && !cloudRow.forgejo_repo_url) {
          await pushForgejoRepoUrl(cloudId, localRow.forgejo_repo_url!);
        }
      } catch (err) { console.warn(`[sync] forgejo url push failed for ${cloudId}:`, err); }
    }
    versions += await pullVersionsFor(localId, cloudId);
    versionsPushed += await pushVersionsFor(localId, cloudId);
    if (cloudUserId) attachments += await pullAttachmentsFor(localId, cloudId, cloudUserId);
  }
  // Session history is synced ONCE, feed-driven (by the folder where sessions
  // actually live), so it's robust to stale/duplicate syncmap entries.
  sessions = await syncAllSessions(map, [...localById.values()], [...cloudById.values()]);
  try { await setLastSyncedAt(new Date().toISOString()); }
  catch (err) { console.warn('[sync] lastSyncedAt write failed:', err); }
  return { projects: projectResult.pulled, versions, versionsPushed, attachments, backends, sessions };
}

/** @deprecated renamed — the flow is bidirectional now */
export const pullDesktopFromCloud = syncDesktopWithCloud;

// Atelier-load hook (P3/v2): on the DESKTOP only, sync cloud projects + versions +
// attachments into the local stores (bidirectional) so the operator's web idea is
// fully present in the desktop Atelier, and desktop versions reach the cloud.
// No-op — returns null — on web, or when not signed in (sync is opt-in).
// The caller refreshes its project list when this resolves non-null.
export async function syncDesktopProjectsOnLoad(): Promise<DesktopSyncResult | null> {
  if (detectPlatform() !== 'desktop') return null;
  if (!(await isCloudSyncAvailable())) return null;
  return syncDesktopWithCloud();
}
